/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
'use client';

import type { Path } from '@ahm/api-wrappers-core';
import { validatePhoneNumberForLogin } from '@ahm/common-helpers';
import { toast } from '@ahm/react-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Accordion,
  Box,
  Button,
  CloseButton,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  rem,
  useMantineTheme,
} from '@mantine/core';
import { closeAllModals, type ContextModalProps } from '@mantine/modals';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { ModalName, showModal } from '..';
import { DropOffFormSection } from './drop-off-form-section';
import { PickupFormSection } from './pickup-form-section';
import type { PathRecipientInfo, PathSenderInfo } from './types';
import { mapRecipientPath, mapSenderPath } from './utils';
import { useUpdateStopInfo } from '@/api/order/stop-point-info/use-update-stop-info';
import { useVerifyStopInfo } from '@/api/order/stop-point-info/use-verify-stop-info';
import InfoIcon from '@/assets/icons/info-blue-icon.svg';
import { useEstimateOrder } from '@/features/booking/api/order/use-estimate-order';
import { useEstimateRequestBody } from '@/hooks/use-estimate-request-body';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

export type EditPathInfoModalProps = Pick<
  ContextModalProps<{
    paths: Path[];
    isPickup: boolean;
    onSave?: (data: { sender: Path; recipient: Path }) => void;
  }>,
  'context' | 'id' | 'innerProps'
>;

interface FormData {
  sender: PathSenderInfo;
  recipient: PathRecipientInfo;
}

export function EditPathInfoModal({
  context: _context,
  id: _id,
  innerProps,
}: EditPathInfoModalProps) {
  const { paths, isPickup, onSave } = innerProps;

  const tCommon = useTranslations('common');
  const tAuth = useTranslations('system.auth');
  const isMobile = useIsMobile();
  const theme = useMantineTheme();

  const { orderDetails: order, serviceTypes } = useBookingStore(
    (state) => state
  );

  const { estimateRequestBody: baseEstimateRequestBody } =
    useEstimateRequestBody({
      orderDetail: order,
      serviceTypes,
    });

  const getEstimateRequestBody = (updatedPaths: Path[]) => {
    if (!baseEstimateRequestBody) {
      return null;
    }
    return {
      ...baseEstimateRequestBody,
      path: updatedPaths,
    };
  };

  const [opened, setOpened] = useState<string | null>(
    isPickup ? 'sender' : 'recipient'
  );

  const senderPath = paths[0];
  const recipientPath = paths[1];

  const { mutate: verifyStopInfo, isPending: isVerifying } = useVerifyStopInfo(
    order?._id ?? ''
  );

  const { mutate: estimateOrder, isPending: isEstimating } = useEstimateOrder();

  const { mutate: updateStopInfo, isPending: isUpdating } = useUpdateStopInfo(
    order?._id ?? '',
    {
      onSuccess: (response, variables) => {
        // Update successful, proceed with save
        if (onSave) {
          const updatedSender = mapSenderPath(senderPath, variables.pickup);
          const updatedRecipient = mapRecipientPath(
            recipientPath,
            variables.dropoffs[0]
          );

          onSave({
            sender: updatedSender,
            recipient: updatedRecipient,
          });
        }
        closeAllModals();

        toast.success({
          title: 'Bạn đã cập nhật đơn hàng thành công.',
          withIcon: true,
        });
      },
      onError: (error) => {
        // Show modal rejecting the changes
        // TODO: Implement error modal
        console.error('Failed to update stop info:', error);
      },
    }
  );

  const senderSchema = useMemo(
    () =>
      z.object({
        name: z.string().optional(),
        mobile: z
          .string()
          .refine((value) => validatePhoneNumberForLogin(value), {
            message: tAuth('invalidPhoneNumber'),
          })
          .optional()
          .or(z.literal('')),
        address: z.string(),
        lat: z.number(),
        lng: z.number(),
      }),
    [tAuth]
  );

  const recipientSchema = useMemo(
    () =>
      z.object({
        name: z.string().optional(),
        mobile: z
          .string()
          .optional()
          .refine((value) => !value || validatePhoneNumberForLogin(value), {
            message: tAuth('invalidPhoneNumber'),
          }),
        address: z
          .string({
            required_error: 'Địa chỉ không được để trống',
            invalid_type_error: 'Địa chỉ không được để trống',
          })
          .min(1, { message: 'Địa chỉ không được để trống' }),
        lat: z.number(),
        lng: z.number(),
        remarks: z.string().optional(),
        tracking_number: z.string().optional(),
        cod: z.number().optional(),
        item_value: z.number().optional(),
      }),
    [tAuth]
  );

  const formSchema = useMemo(
    () =>
      z.object({
        sender: senderSchema,
        recipient: recipientSchema,
      }),
    [senderSchema, recipientSchema]
  );

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      sender: {
        lat: senderPath?.lat || 0,
        lng: senderPath?.lng || 0,
        name: senderPath?.name || '',
        mobile: senderPath?.mobile || '',
        address: senderPath?.address || '',
        remarks: senderPath?.remarks || '',
      },
      recipient: {
        lat: recipientPath?.lat || 0,
        lng: recipientPath?.lng || 0,
        name: recipientPath?.name || '',
        mobile: recipientPath?.mobile || '',
        address: recipientPath?.address || '',
        remarks: recipientPath?.remarks || '',
        cod: recipientPath?.cod || 0,
        tracking_number: recipientPath?.tracking_number || '',
        item_value: recipientPath?.item_value || 0,
      },
    },
  });

  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = methods;

  if (!paths || paths?.length < 2) {
    console.log('No paths provided, returning null');
    return null;
  }

  const onSubmit = (data: FormData) => {
    // Rule:
    // sender: only remarks, name, mobile
    // recipient: lat, lng, name, mobile, address, remarks

    const requestBody = {
      pickup: {
        name: data.sender.name ?? '',
        mobile: data.sender.mobile ?? '',
        remarks: data.sender.remarks,
      },
      dropoffs: [
        {
          address: data.recipient.address ?? '',
          lat: data.recipient.lat,
          lng: data.recipient.lng,
          name: data.recipient.name ?? '',
          mobile: data.recipient.mobile ?? '',
          remarks: data.recipient.remarks,
          path_index: 1,
          cod: data.recipient.cod,
        },
      ],
    };

    // Check if recipient address has changed (lat/lng)
    const hasAddressChanged =
      data.recipient.lat !== recipientPath.lat ||
      data.recipient.lng !== recipientPath.lng;

    const proceedWithUpdate = () => {
      // Step 1: Verify the stop info first
      verifyStopInfo(requestBody, {
        onSuccess: () => {
          // If address changed, get new estimate price
          if (hasAddressChanged) {
            // Step 2: Estimate new price using proper body construction
            const updatedPaths = [
              {
                ...senderPath,
                name: data.sender.name,
                mobile: data.sender.mobile,
                remarks: data.sender.remarks,
              },
              {
                ...recipientPath,
                lat: data.recipient.lat,
                lng: data.recipient.lng,
                name: data.recipient.name,
                mobile: data.recipient.mobile,
                address: data.recipient.address,
                remarks: data.recipient.remarks,
              },
            ];

            const estimateRequestBody = getEstimateRequestBody(updatedPaths);

            if (!estimateRequestBody) {
              toast.error({
                title: 'Lỗi hệ thống',
                message:
                  'Không thể tạo yêu cầu ước tính giá. Vui lòng thử lại.',
                withIcon: true,
              });
              return;
            }

            estimateOrder(estimateRequestBody, {
              onSuccess: (estimateResponse) => {
                const oldPrice = (order as any)?.total_price || 0;
                const newPrice = estimateResponse?.[0]?.data?.total_price || 0;

                if (oldPrice !== newPrice) {
                  // Show price comparison modal
                  showModal(
                    ModalName.PriceComparison,
                    {
                      newPath: updatedPaths as Path[],
                      oldPrice,
                      newPrice,
                      onConfirm: () => {
                        // Step 3: If user confirms, proceed with update
                        updateStopInfo(requestBody);
                      },
                    },
                    {
                      p: 0,
                      w: isMobile ? '100%' : rem(500),
                      centered: true,
                      withCloseButton: false,
                      styles: {
                        body: {
                          position: 'relative',
                          padding: 0,
                          background: 'white',
                        },
                      },
                    }
                  );

                  return;
                }

                // No price change, proceed directly
                updateStopInfo(requestBody);
              },
              onError: (error) => {
                toast.error({
                  title: 'Lỗi ước tính giá cước',
                  message: error.response?.data?.description,
                  withIcon: true,
                });
              },
            });
          } else {
            // No address change, proceed directly
            updateStopInfo(requestBody);
          }
        },
        onError: (error) => {
          toast.error({
            title:
              error.response?.data.title || 'Lỗi xác thực thông tin điểm dừng',
            message: error.response?.data?.description,
            withIcon: true,
          });
        },
      });
    };

    proceedWithUpdate();
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={0}>
          <Box pos="sticky" top={0} bg="white" zidx={1}>
            <Group justify="space-between" align="center" p={rem(16)}>
              <Text fz={rem(20)} lh={rem(24)} fw={700}>
                Chỉnh sửa thông tin
              </Text>
              <CloseButton
                iconSize={rem(24)}
                variant="transparent"
                onClick={closeAllModals}
                c="neutral.6"
              />
            </Group>
          </Box>
          <Divider />

          <Stack p={rem(16)} gap={rem(12)}>
            <Group
              gap={rem(8)}
              p={rem(12)}
              bg="blue.0"
              wrap="nowrap"
              align="flex-start"
              style={{
                borderRadius: rem(8),
              }}
            >
              <InfoIcon />
              <Text c="neutral.6" fw={500} flex={1}>
                Bạn chỉ có thể cập nhật thông tin một lần. Trường hợp bạn{' '}
                <Box component="span" c="neutral.6" fw={700}>
                  thay đổi địa chỉ
                </Box>
                , giá cước và tài xế cũng có thể thay đổi theo.
              </Text>
            </Group>

            <Accordion
              value={opened}
              onChange={setOpened}
              styles={{
                item: {
                  border: `1px solid ${theme.colors.neutral[2]}`,
                  borderRadius: rem(8),
                },
                label: {
                  paddingRight: rem(8),
                },
              }}
            >
              <Accordion.Item value="sender">
                <Accordion.Control
                  aria-label={`sender`}
                  chevron={
                    <ChevronDown size={24} color={theme.colors.primary[7]} />
                  }
                >
                  <Group justify="space-between" w="100%">
                    <Text fw={600}>Người gửi</Text>

                    <Text size="sm" fw={700} c="primary.6">
                      {opened === `sender` ? 'Thu gọn' : 'Mở rộng'}
                    </Text>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <PickupFormSection />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>

            {/* Recipient */}
            <Accordion
              value={opened}
              onChange={setOpened}
              styles={{
                item: {
                  border: `1px solid ${theme.colors.neutral[2]}`,
                  borderRadius: rem(8),
                },
                label: {
                  paddingRight: rem(8),
                },
              }}
            >
              <Accordion.Item value="recipient">
                <Accordion.Control
                  aria-label="recipient"
                  chevron={
                    <ChevronDown size={24} color={theme.colors.primary[7]} />
                  }
                >
                  <Group justify="space-between" w="100%">
                    <Text fw={600}>Người nhận</Text>

                    <Text size="sm" c="primary.6" fw={700}>
                      {opened === 'recipient' ? 'Thu gọn' : 'Mở rộng'}
                    </Text>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <DropOffFormSection />
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion>
          </Stack>

          {/* Submit Button */}
          <SimpleGrid
            cols={isMobile ? 1 : 2}
            pos="sticky"
            bottom={0}
            bg="white"
            p={rem(24)}
            spacing={rem(16)}
            style={{
              zIndex: 1,
              boxShadow: '0 -2px 4px 0 rgba(0, 0, 0, 0.10)',
            }}
          >
            {!isMobile ? (
              <Button
                variant="outline"
                h={rem(48)}
                c="neutral.7"
                fw={700}
                fz={rem(16)}
                styles={{
                  root: {
                    borderColor: theme.colors.neutral[3],
                  },
                }}
                onClick={closeAllModals}
              >
                {tCommon('actions.cancel')}
              </Button>
            ) : null}

            <Button
              type="submit"
              fullWidth
              h={rem(48)}
              fw={700}
              fz={rem(16)}
              disabled={!isDirty || !isValid}
              loading={isVerifying || isEstimating || isUpdating}
            >
              {tCommon('actions.update')}
            </Button>
          </SimpleGrid>
        </Stack>
      </form>
    </FormProvider>
  );
}
